<template>
  <h1>Create a Spellingframe account</h1>
  <div class="sm:tw-grid sm:tw-grid-cols-2 sm:tw-gap-8">
    <div>
      <RegisterResume>
        <template v-slot:title> School Account </template>
        <template v-slot:default>
          <AccountShortDescription type="school" />
        </template>
        <template v-slot:switch>
          <div v-if="mfTokenData">
            If you would prefer to register as an individual user please
            <ContactLink /> for further assistance.
          </div>
          <SfRouterLink v-else :to="'/register/individual'">
            Switch to individual account
          </SfRouterLink>
        </template>
      </RegisterResume>
    </div>
    <div>
      <form ref="registerForm" @submit.prevent="submit">
        <fieldset :disabled="status == 'loaded'">
          <FormInput v-model="formData.fields.school_n">
            <label> School name* </label>
            <input
              type="text"
              v-model="formData.fields.school_n.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput v-model="formData.fields.first_name">
            <label> Your first name* </label>
            <input
              type="text"
              v-model="formData.fields.first_name.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput v-model="formData.fields.last_name">
            <label> Your last name* </label>
            <input
              type="text"
              v-model="formData.fields.last_name.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput
            v-model="formData.fields.email"
            :custom-errors="{ exists: 'A user with this email already exists' }"
          >
            <label> Your email address* </label>
            <div v-if="mfTokenData">
              {{ formData.fields.email.value }}
            </div>
            <input
              v-else
              type="text"
              v-model="formData.fields.email.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput
            v-model="formData.fields.adminEmailConfirm"
            v-if="!mfTokenData"
            :all-fields="formData.fields"
            :custom-errors="{ match: 'Emails don\'t match' }"
          >
            <label> Confirm your email address* </label>
            <input
              type="text"
              v-model="formData.fields.adminEmailConfirm.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput v-model="formData.fields.address1">
            <label> School address line 1* </label>
            <input
              type="text"
              v-model="formData.fields.address1.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput v-model="formData.fields.address2">
            <label> School address line 2 </label>
            <input
              type="text"
              v-model="formData.fields.address2.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput v-model="formData.fields.address3">
            <label> School address line 3 </label>
            <input
              type="text"
              v-model="formData.fields.address3.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput
            v-model="formData.fields.postcode"
            :custom-errors="{
              exists: 'A school with this postcode already exists',
            }"
          >
            <label> Post code* </label>
            <input
              type="text"
              v-model="formData.fields.postcode.value"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput
            v-model="formData.fields.password"
            :custom-errors="{
              minLength: 'passwords must be at least 6 characters long',
            }"
          >
            <label> Password* </label>
            <input
              type="password"
              v-model="formData.fields.password.value"
              autocomplete="off"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
          <FormInput v-model="formData.fields.passwordConfirm">
            <label> Confirm password* </label>
            <input
              type="password"
              v-model="formData.fields.passwordConfirm.value"
              autocomplete="off"
              class="sf-form-control tw-w-full"
            />
          </FormInput>
        </fieldset>
        <div class="tw-mb-4">
          I have read, understood, and agree to the
          <a href="/sitemedia/Spellingframe_Terms_and_Conditions_FULL_v1.pdf" target="sf_tos"
            >terms and conditions</a
          >
        </div>
        <div>
          <button
            type="submit"
            :disabled="status == 'loaded'"
            class="sf-btn-primary"
          >
            Register
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import SfRouterLink from "@/components/shared/SfRouterLink.vue";
import { LoadingStatus, SfFormData } from "@/types/generalTypes";
import submitForm from "@/func/form/submitForm";
import FormInput from "../ui/FormInput.vue";
import RegisterResume from "./RegisterResume.vue";
import AccountShortDescription from "./AccountShortDescription.vue";
import ContactLink from "../ui/ContactLink.vue";
import { MfTokenData } from "@/types/mfTypes";

export default defineComponent({
  components: {
    SfRouterLink,
    FormInput,
    RegisterResume,
    AccountShortDescription,
    ContactLink,
  },
  props: {
    mfTokenData: { type: Object as PropType<MfTokenData | null> },
  },
  data() {
    return {
      status: "inited" as LoadingStatus,
      formData: {
        fields: {
          school_n: { value: "", required: true },
          first_name: { value: "", required: true },
          last_name: { value: "", required: true },
          email: { value: "", required: true, isEmail: true },
          adminEmailConfirm: { value: "", match: "email" },
          address1: { value: "", required: true },
          address2: { value: "" },
          address3: { value: "" },
          postcode: { value: "", required: true },
          password: { value: "", required: true, minLength: 6 },
          passwordConfirm: { value: "", match: "password" },
        },
      } as SfFormData,
    };
  },
  computed: {
    mfToken() {
      return this.$route.query.mft || "";
    },
  },
  mounted() {
    if (this.mfTokenData && this.mfTokenData.userData) {
      this.formData.fields.school_n.value = this.mfTokenData.userData.school;
      this.formData.fields.first_name.value =
        this.mfTokenData.userData.first_name;
      this.formData.fields.last_name.value =
        this.mfTokenData.userData.last_name;
      this.formData.fields.email.value = this.mfTokenData.userData.email;
      this.formData.fields.adminEmailConfirm.value =
        this.mfTokenData.userData.email;
      this.formData.fields.address1.value = this.mfTokenData.userData.address1;
      this.formData.fields.address2.value = this.mfTokenData.userData.address2;
      this.formData.fields.address3.value = this.mfTokenData.userData.county;
      this.formData.fields.postcode.value = this.mfTokenData.userData.postcode;
    }
  },
  methods: {
    async submit() {
      if (this.status == "loaded") {
        return;
      }
      this.status = "loading";
      const extraFields = {
        time_offset: new Date().getTimezoneOffset(),
        mfToken: this.mfToken,
      };
      const resData = await submitForm("register/school", this.formData, null, {
        extraFields,
      });
      this.formData = resData.formData;
      if (resData.response.success && resData.response.data?.success) {
        this.status = "loaded";
        window.location.href = "/dashboard";
      } else {
        this.status = "error";
        this.$nextTick(this.scrollToError);
      }
    },
    scrollToError() {
      const form = this.$refs.registerForm as HTMLElement
      const elements = form.getElementsByClassName('has-error');
      if(elements.length > 0) {
        elements[0].scrollIntoView({ behavior: 'smooth', block: 'start'});
      }
    }
  },
});
</script>
